// AddViewCardModalOption.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './AddViewCardModalOption.css';

// Icons
import Icon from 'svg/Icon';

/**
 * AddViewCardModalOption
 *
 * Renders one selectable row within the add-view modal.
 *
 * @param {string} iconKey - Icon key shown for the option.
 * @param {string} title - Primary label for the row.
 * @param {string} note - Secondary helper text for the row.
 * @param {function} onClick - Called when the option is chosen.
 *
 * @returns {ReactNode} The rendered option row.
 */
const AddViewCardModalOption = React.memo(({
  iconKey,
  title,
  note,
  onClick,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [hovered, setHovered] = useState(false);

  // ----- RENDER ----- //

  return (
    <button
      className="add-view-card-modal-option-container"
      style={{
        backgroundColor: hovered ? theme.buttonHoverBackgroundColor : theme.panel,
        color: hovered ? theme.buttonHoverLabelColor : theme.foreground,
      }}
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >

      {/* ICON */}
      <div className="add-view-card-modal-option-icon">
        <Icon
          iconKey={iconKey || 'view_kanban'}
          width={20}
          height={20}
          color={hovered ? theme.buttonHoverIconColor : theme.foregroundMuted}
        />
      </div>

      {/* CONTENT */}
      <div className="add-view-card-modal-option-content">

        {/* TITLE */}
        <div className="add-view-card-modal-option-title">
          {title}
        </div>

        {/* NOTE */}
        {note && (
          <div
            className="add-view-card-modal-option-note"
            style={{
              color: hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted,
            }}
          >
            {note}
          </div>
        )}
      </div>

      {/* TRAILING */}
      <div className="add-view-card-modal-option-trailing">
        <Icon
          iconKey="chevron_right"
          width={18}
          height={18}
          color={hovered ? theme.buttonHoverIconColor : theme.foregroundMuted}
        />
      </div>
    </button>
  );
});

// Dev Tools
AddViewCardModalOption.displayName = 'AddViewCardModalOption';

export default AddViewCardModalOption;
